
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { FormData } from '@/types/onboarding';

interface OnboardingCardProps {
  currentStep: number;
  question: {
    headline: string;
    subtext: string;
    type: 'input' | 'radio';
    field?: keyof FormData;
    options?: string[];
  };
  formData: FormData;
  onInputChange: (field: keyof FormData, value: string) => void;
  cardTransitionState: 'active' | 'exiting' | 'entering';
  transitionDirection: 'forward' | 'backward';
}

const OnboardingCard = ({
  currentStep,
  question,
  formData,
  onInputChange,
  cardTransitionState,
  transitionDirection
}: OnboardingCardProps) => {
  const getCardTransitionClasses = () => {
    if (cardTransitionState === 'active') {
      return 'card-active';
    }
    if (cardTransitionState === 'exiting') {
      return transitionDirection === 'forward' ? 'animate-slide-out-left' : 'animate-slide-out-right';
    }
    if (cardTransitionState === 'entering') {
      return transitionDirection === 'forward' ? 'animate-slide-in-right' : 'animate-slide-in-left';
    }
    return '';
  };

  return (
    <div className="relative mb-8">
      <div className={`bg-white rounded-3xl p-6 shadow-2xl transition-all duration-400 ease-out min-h-[350px] md:min-h-[400px] max-h-[80vh] overflow-y-auto ${getCardTransitionClasses()}`}>
        <h2 className="text-2xl font-black text-purple-900 mb-3 leading-tight">
          {question.headline}
        </h2>
        <p className="text-gray-600 mb-6 leading-relaxed">
          {question.subtext}
        </p>
        
        {/* Question Content */}
        {currentStep === 0 && (
          <div className="space-y-4">
            <div className="animate-slide-up">
              <Input
                type="text"
                value={formData.firstName}
                onChange={(e) => onInputChange('firstName', e.target.value)}
                placeholder="Your first name"
                className="w-full px-4 py-3 text-lg rounded-xl border-2 border-gray-200 focus:border-purple-400 focus:ring-0 transition-all duration-300"
                autoFocus
              />
            </div>
            <div className="animate-slide-up">
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => onInputChange('email', e.target.value)}
                placeholder="your@email.com"
                className="w-full px-4 py-3 text-lg rounded-xl border-2 border-gray-200 focus:border-purple-400 focus:ring-0 transition-all duration-300"
              />
            </div>
          </div>
        )}

        {currentStep > 0 && question.type === 'radio' && (
          <div className="space-y-1">
            <RadioGroup
              value={formData[question.field as keyof FormData]}
              onValueChange={(value) => onInputChange(question.field as keyof FormData, value)}
              className="space-y-2"
            >
              {question.options?.map((option, index) => (
                <div key={index} className="animate-slide-up" style={{ animationDelay: `${index * 50}ms` }}>
                  <div className="flex items-start space-x-3 p-3 rounded-xl hover:bg-gradient-to-r hover:from-purple-50 hover:to-blue-50 transition-all duration-300 cursor-pointer group border-2 border-transparent hover:border-purple-200">
                    <RadioGroupItem value={option} id={`option-${index}`} className="mt-1 transition-all duration-300 group-hover:scale-110" />
                    <Label htmlFor={`option-${index}`} className="text-lg text-gray-700 leading-relaxed cursor-pointer flex-1 group-hover:text-purple-700 transition-colors duration-300">
                      {option}
                    </Label>
                  </div>
                </div>
              ))}
            </RadioGroup>

            {/* Conditional fields */}
            {currentStep === 1 && formData.motivation === "Other comments" && (
              <div className="mt-4 animate-scale-in">
                <Textarea
                  value={formData.motivationOther || ''}
                  onChange={(e) => onInputChange('motivationOther', e.target.value)}
                  placeholder="Please tell us more about what motivated you..."
                  className="w-full px-4 py-3 text-lg rounded-xl border-2 border-gray-200 focus:border-purple-400 focus:ring-0 transition-all duration-300 min-h-[100px]"
                />
              </div>
            )}

            {currentStep === 2 && formData.experience === "I'm currently using a habit app" && (
              <div className="mt-4 animate-scale-in">
                <Input
                  type="text"
                  value={formData.currentHabitApp || ''}
                  onChange={(e) => onInputChange('currentHabitApp', e.target.value)}
                  placeholder="Which habit app are you currently using?"
                  className="w-full px-4 py-3 text-lg rounded-xl border-2 border-gray-200 focus:border-purple-400 focus:ring-0 transition-all duration-300"
                />
              </div>
            )}

            {currentStep === 4 && formData.useCase === "Others" && (
              <div className="mt-4 animate-scale-in">
                <Textarea
                  value={formData.useCaseOther || ''}
                  onChange={(e) => onInputChange('useCaseOther', e.target.value)}
                  placeholder="Please describe the area where you'd like Show Up! to help you..."
                  className="w-full px-4 py-3 text-lg rounded-xl border-2 border-gray-200 focus:border-purple-400 focus:ring-0 transition-all duration-300 min-h-[100px]"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingCard;
